import React from "react";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  stepConnectorClasses,
  stepIconClasses,
  stepLabelClasses,
} from "@mui/material";
import ReactCodeInput from "react-verification-code-input";
import { v4FlatWinsAtBreakpointsSx } from "../../../theme/v4CompatSx";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS / PAYMENTS ESCALATION CLASS.** This is the shared style
 * sheet for the whole signup funnel (/try, /buy, /usemycode, /referral) — the
 * surface that creates applications, verifies phone + email, and hands off to
 * checkout. The conversion is styling-only: no handler, validator, state
 * value, prop, condition or DOM node moved anywhere in this batch.
 *
 * SHAPE CHANGE (mechanical, not behavioural): v4 exported a single
 * `makeStyles` hook and four components called `useStyles()`. `makeStyles` has
 * no v5+ equivalent in this repo (D1 deliberately avoided `@mui/styles`), so
 * the sheet becomes named `sx` constants + `styled()` components imported by
 * the same four files. Every export below is named after the v4 key it
 * replaces, so the diff stays traceable rule for rule.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1). `formTitle` was `{}` — an empty rule
 * that emitted a class name and no declarations. The following keys were
 * declared in the v4 `useStyles` object but never referenced from any JSX in
 * the funnel, so they emitted CSS that no element ever carried:
 *   accountTable, alertBox, alertBoxTitle, bodySml, button, codeDurationString,
 *   divider, errorAlert, forgotPassword, giftcardCodeContainer, giftcardFooter,
 *   formHeader, formSubHeader, phoneHelperText, phoneInput, phoneInputError,
 *   registerMobile, stepContent, table, tableText, termsCheckbox, termsLabel,
 *   validationError.
 * Carrying them across would be inventing styles rather than porting them.
 * (Verified by grepping `classes.` across all four consumers before deleting.)
 *
 * Every literal `@media (min-width:600px)` / `(min-width:960px)` becomes
 * `theme.breakpoints.up("sm")` / `("md")`; `themeV5` pins sm/md to the v4
 * values (600/960), so the emitted query text is identical. Key order inside
 * each object is preserved verbatim — emotion resolves same-specificity
 * conflicts by source order exactly as JSS did, so re-ordering "for tidiness"
 * would be a rendering change.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host elements, which have no `sx` prop (guide §1.2 row 2)
 * ---------------------------------------------------------------------- */

// was: useStyles().background / useStyles().noBackground, applied to the same
// bare <div> as `background ? classes.background : classes.noBackground`.
// The two v4 rules are byte-identical apart from the `min-width:600px` block,
// so they port to one styled div with that block gated on a transient prop —
// which also keeps the element type stable across renders (swapping between
// two styled components would remount the whole form subtree).
export const SignUpBackground = styled("div", {
  shouldForwardProp: (prop) => prop !== "withImage",
})<{ withImage?: boolean }>(({ theme, withImage }) => ({
  position: "relative",
  backgroundImage: "none",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundAttachment: "scroll",
  width: "100%",
  height: "auto",
  display: "flex",
  flexGrow: 1,
  boxShadow: "none",
  backgroundColor: theme.palette.background.ffNavy,
  ...(withImage
    ? {
        [theme.breakpoints.up("sm")]: {
          backgroundImage: `url(https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/usemycode_bg.jpg)`,
          boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.6)",
        },
      }
    : {}),
}));

// was: useStyles().form. The v4 class went on a bare <div> in one place and a
// bare <form> in another, so it needs one styled component per tag.
export const FormBody = styled("div")({
  width: "100%", // Fix IE 11 issue.
});
export const FormElement = styled("form")({
  width: "100%", // Fix IE 11 issue.
});

// was: useStyles().primaryColor, applied to bare <span>s inside a Typography.
export const PrimaryColorSpan = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
}));

// was: useStyles().successLogo, applied to a bare <img>.
export const SuccessLogo = styled("img")({
  maxWidth: "140px",
  height: "auto",
});

// `react-verification-code-input` ships a broken declaration (`declare const
// ReactCodeInput: ReactCodeInput`, an object type rather than a component),
// which is why the v4 call sites carried a `@ts-ignore`. Casting once here
// lets `styled()` wrap it — same approach as common/CodeForm.tsx (T1).
type CodeInputProps = {
  className?: string;
  type?: string;
  fields?: number;
  disabled?: boolean;
  onComplete?: (value: string) => void;
};
const CodeInput =
  ReactCodeInput as unknown as React.ComponentType<CodeInputProps>;

// was: useStyles().verificationCodeContainer. The target is a third-party
// component that takes a bare `className` and has no `sx` prop, so this is
// `styled()` (guide §1.2). Wrapping the component rather than adding a wrapper
// <div> keeps the DOM identical to v4.
export const VerificationCodeInput = styled(CodeInput)(({ theme }) => ({
  width: "100% !important",
  fontWeight: 600,
  "& input": {
    height: "56px !important",
    width: "15.6% !important",
    borderTop: "none",
    borderRight: "none",
    borderLeft: "none",
    borderTopLeftRadius: "0px !important",
    borderBottomLeftRadius: "0px !important",
    borderTopRightRadius: "0px !important",
    borderBottomRightRadius: "0px !important",
    borderBottom: 0,
    backgroundColor: "#444",
    margin: "0 0.5% 10px 0.5%",
    fontSize: "2rem",
    fontWeight: 500,
    color: "#fff",
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    "&:focus": {
      border: "1px solid #99ca3c",
      caretColor: "#fff",
      "&:last-child": {
        borderRight: "1px solid #99ca3c",
      },
    },
    "&:last-child": {
      borderRight: "none",
    },
  },
  [theme.breakpoints.up("sm")]: {
    width: "80% !important",
    margin: "0 10%",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().redeemGiftcardContainer, on the <Container>.
//
// PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
// own breakpoint rule"): `padding: 0` has to be re-stated at `up("sm")`.
// `Container` sets `padding-left/right: 24px` there, and in v9 that @media
// block is emitted AFTER this class's flat declarations, so above 600px the
// gutters came back — measured as the signup card losing 48px of width
// (600px → 552px on /try, /buy and /usemycode, and the form inside it
// 512px → 464px). In v4 the makeStyles sheet had a later JSS index and won at
// every width.
export const redeemGiftcardContainerSx: SxProps<Theme> = (theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      width: "100%",
      padding: 0,
      margin: 0,
      [theme.breakpoints.up("sm")]: {
        margin: "0 auto",
      },
      display: "flex",
      justifyContent: "center",
    },
    ["padding"],
    ["sm"],
  );

// was: useStyles().backdrop, on the <Backdrop>.
export const backdropSx: SxProps<Theme> = (theme) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: "#fff",
});

/**
 * was: useStyles().paper, on the <Grid container>, merged with the three
 * alignment props the same element carried in v4 (`justify="center"`,
 * `alignContent="center"`, `alignItems="center"`).
 *
 * v9's `Grid` accepts only `size`/`offset`/`spacing`/`container`/`direction`/
 * `wrap`/`columns` — `justify` (v4's spelling of `justifyContent`),
 * `alignContent` and `alignItems` are not props any more and would be
 * forwarded to the DOM as unknown attributes (guide §2). This element IS a
 * flex container, so all three really did render in v4 and move into `sx`
 * rather than being dropped. They cannot collide with the `paper` rules —
 * `paper` declares none of the three.
 *
 * `backgroundColor: "none"` is invalid CSS and was dropped by the browser in
 * v4 too; carried over verbatim rather than "fixed", which would be a visual
 * change. `borderRadius` is written as the literal `"0px"`, not the bare
 * number — `sx`'s `borderRadius` multiplies through `shape.borderRadius`
 * (guide §2), so `0` would be harmless here but `"0px"` keeps the rule honest.
 */
export const paperSx: SxProps<Theme> = (theme) => ({
  justifyContent: "center",
  alignContent: "center",
  alignItems: "center",
  width: "100%",
  borderRadius: "0px",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  paddingTop: theme.spacing(0),
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: "none",
  [theme.breakpoints.up("sm")]: {
    backgroundColor: "rgba(23,23,23,0.9)",
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
});

/**
 * was: useStyles().stepper, on the <Stepper>.
 *
 * §1.2b hit — the v4 rule reached into its children with hard-coded global
 * `.MuiStepLabel-label` / `.MuiStepLabel-active` / `.MuiStepLabel-completed` /
 * `.MuiStepConnector-line` / `.MuiStepIcon-text` selectors. Those children
 * convert in this same batch, so under the `FfV5` prefix the v4 strings would
 * silently stop matching and the stepper would lose its white/lime labels.
 * The names are read off the classes objects rather than hand-written, which
 * covers the prefix and fails loudly if a slot ever disappears.
 *
 * ♦ NEW DELTA (see guide §2, "state slots are global `Mui-*`"): reading the
 * names off the classes object shows that `stepLabelClasses.active` and
 * `.completed` are NOT `FfV5StepLabel-active/-completed` — they are the bare
 * GLOBAL STATE classes `Mui-active` / `Mui-completed`, which
 * `generateUtilityClass` hard-codes and the `FfV5` generator never sees. Two
 * consequences, both handled here:
 *   1. re-prefixing `.MuiStepLabel-active` would have produced a selector that
 *      matches nothing;
 *   2. v9 puts those state classes on MORE slots than v4 did — v4 applied
 *      `active`/`completed` to the label span only, v9 also applies them to the
 *      `iconContainer`. So each is compounded with the `label` class, which
 *      targets exactly the element v4's rule targeted and nothing else.
 *
 * The compounding also makes the cascade robust: v4 relied on source order
 * (`.stepper .MuiStepLabel-label` and `.stepper .MuiStepLabel-active` tie at
 * (0,2,0), and `active` was declared second), whereas `.label.Mui-active` is
 * (0,3,0) and wins on specificity. Same rendering, one less trap.
 *
 * PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
 * own breakpoint rule"): `paddingTop`/`paddingBottom` have to be re-stated at
 * `up("sm")` and `up("md")`. `themeV5`'s `MuiStepper.styleOverrides.root`
 * declares `padding: "24px 0"` at both, and in v9 those @media blocks are
 * emitted AFTER this class's flat declarations — so the stepper's vertical
 * padding went 16px → 24px above 600px, pushing everything below it down 16px.
 * In v4 the makeStyles sheet had a later JSS index and won at every width.
 */
export const stepperSx: SxProps<Theme> = (theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      backgroundColor: "transparent",
      width: "100%",
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      [`& .${stepLabelClasses.label}`]: {
        color: theme.palette.common.white,
      },
      [`& .${stepLabelClasses.label}.${stepLabelClasses.active}`]: {
        color: theme.palette.background.ffGreen,
      },
      [`& .${stepLabelClasses.label}.${stepLabelClasses.completed}`]: {
        color: theme.palette.background.ffGreen,
      },
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: "#777",
      },
      [`& .${stepIconClasses.text}`]: {
        fill: theme.palette.background.ffNavy,
      },
    },
    ["paddingTop", "paddingBottom"],
    ["sm", "md"],
  );

// was: useStyles().stepperLabel, on each <StepLabel>. Both declarations carry
// `!important` in v4 and keep it here; the label span sets its own colour from
// the rule above, so this only ever reached the StepLabel root — unchanged.
export const stepperLabelSx: SxProps<Theme> = {
  fontSize: "0.9rem !important",
  color: "#fff !important",
};

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"`
// is NOT in the v4 rule. Every <Button> carrying this class omits the `color`
// prop, so in v4 it was `color="default"` → `color: theme.palette.text.primary`.
// v5+ deleted the `default` colour and made `primary` the default, which on
// this theme paints the label #99ca3c on the lime `typography.button`
// background — an invisible "Next" / "Check Giftcard" button mid-signup.
export const submitSx: SxProps<Theme> = (theme) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: "100%",
  color: "text.primary",
});

/**
 * was: `className={`${classes.secSubmit} ${classes.goBack}`}` — TWO v4 keys on
 * ONE element (guide §2, "Two `makeStyles` keys on one element"). Both classes
 * applied at equal specificity, so JSS resolved conflicts by the order of the
 * keys in the `useStyles` object: `secSubmit` was declared first, `goBack`
 * second, so `goBack` won.
 *
 * The one real conflict is the border: `secSubmit` set `borderColor: #e9e9e9`
 * and `goBack` then set the `border: none` shorthand, which resets it — so
 * production has always rendered this button borderless. Listing the
 * `secSubmit` keys first and the `goBack` keys second reproduces that exactly;
 * re-ordering them would put a grey border back on the "Resend Code" button.
 * The same rule applies inside `&:hover`, where `goBack`'s `border: none`
 * again lands after `secSubmit`'s `borderColor`, while `secSubmit`'s hover
 * `color` survives untouched.
 *
 * No Button colour restore here: `secSubmit` pins `color` itself.
 */
export const secSubmitGoBackSx: SxProps<Theme> = {
  // --- secSubmit (declared first in v4) ---
  marginLeft: 0,
  marginRight: 0,
  width: "100%",
  borderColor: "#e9e9e9",
  color: "#e9e9e9",
  // --- goBack (declared second in v4 — wins any conflict) ---
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  marginTop: "2px",
  float: "left",
  marginBottom: "10px",
  paddingLeft: "0",
  "&:hover": {
    // secSubmit's hover, then goBack's hover — same order as the v4 sheet.
    borderColor: "#99ca3c",
    color: "#99ca3c",
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
  },
};

// was: useStyles().bodyText.
export const bodyTextSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.common.white,
  marginBottom: "10px",
  lineHeight: "1.2rem",
  "& a": {
    textDecoration: "underline",
    color: theme.palette.common.white,
  },
});

/**
 * was: `className={`${classes.bodyText} ${classes.resetCode}`}` — the second
 * "two keys on one element" case in this sheet. `bodyText` is declared before
 * `resetCode` in the v4 object, so `resetCode` wins the `color` conflict and
 * the "Wrong Code? Reset Now" line renders lime, not white. Same key order
 * here reproduces that; swapping them would turn the link white.
 */
export const bodyTextResetCodeSx: SxProps<Theme> = (theme) => ({
  // --- bodyText (declared first in v4). Its `color: common.white` is NOT
  // carried: `resetCode` re-declares `color` and won in v4, so restating it
  // here would only be a duplicate key that JS drops anyway. ---
  marginBottom: "10px",
  lineHeight: "1.2rem",
  "& a": {
    textDecoration: "underline",
    color: theme.palette.common.white,
  },
  // --- resetCode (declared second in v4 — won the `color` conflict) ---
  color: theme.palette.background.ffGreen,
  textDecoration: "underline",
  cursor: "pointer",
  fontWeight: 500,
  marginTop: theme.spacing(4),
  "&:hover": {
    cursor: "pointer",
  },
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(2),
  },
});

// was: useStyles().submittedMobileNumber.
export const submittedMobileNumberSx: SxProps<Theme> = (theme) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  textAlign: "center",
  fontSize: "1.4rem",
  color: theme.palette.common.white,
});

// was: useStyles().incorrectMobileNumber.
export const incorrectMobileNumberSx: SxProps<Theme> = (theme) => ({
  marginTop: theme.spacing(0),
  marginBottom: theme.spacing(3),
  textAlign: "center",
  color: theme.palette.background.ffGreen,
  textDecoration: "underline",
  cursor: "pointer",
  fontWeight: 500,
});

// was: useStyles().formField. The two descendant selectors are *element*
// selectors (`label`, `input`), not `.Mui*` ones, so the §1.2b class-prefix
// trap does not apply — they keep matching the v9 TextField's label and input.
//
// Declared as a plain object (palette paths rather than a theme callback) so
// consumers can spread it together with the Grid-item padding: v9's `Grid`
// types its `sx` as a single `SystemStyleObject`, NOT `SxProps`, so the
// `sx={[a, b]}` array form other MUI components accept does not compile there.
//
// PARITY RESTORE (T2 visual gate) — `&&` instead of `&` on the label rule.
// The theme's own `MuiFormControl.styleOverrides.root` → `"& label"` (color
// `#171717`) ties with this one at (0,1,1) on the TextField's label, and only
// source order separated them. v4's JSS put every `makeStyles` sheet after
// MUI's, so the file rule won; emotion emits in render order and the
// FormControl is a CHILD of the element carrying this rule, so its class lands
// later and the theme's navy wins on a navy panel. `&&` = (0,2,1), restoring
// v4's outcome by specificity instead of by insertion order. Measured in
// Chromium on both themes' emitted CSS: `rgb(255,255,255)` v4 vs
// `rgb(23,23,23)` v9-before-fix. Same fix as `SignIn/EmailSignIn.tsx`.
export const formFieldSx = {
  "&& label": {
    color: "common.white",
  },
  "& input": {
    backgroundColor: "#444",
    color: "#fff",
    borderColor: "#444",
  },
};

// was: useStyles().formFieldSetLarge, on the <Grid item> wrapping the phone
// field. The `.react-tel-input .*` selectors are third-party class names, not
// MUI ones, so they are unaffected by the `FfV5` prefix. They duplicate rules
// that TwoStepPhoneInput's own wrapper declares (converted in T1) with
// identical values, so the two are order-independent.
// A plain object for the same reason as `formFieldSx` above. `margin` is the
// literal string `theme.spacing(1, 0, 0, 0)` produced on this theme (8px unit),
// so the emitted declaration is byte-identical to v4's.
//
// Deliberately still a single `&` — unlike `formFieldSx` above. The label this
// rule paints is react-phone-input-2's own `.special-label` (its text comes
// from an App.css `::before`), and that subtree contains no MUI `FormControl`,
// so the theme's `MuiFormControl.root` → `"& label"` rule has nothing to
// match here and there is no tie to break. Confirmed on the T2 capture: the
// buy-now "Country Code and Mobile Number" label stayed white.
export const formFieldSetLargeSx = {
  margin: "8px 0px 0px 0px",
  "& label": {
    color: "common.white",
  },
  "& .react-tel-input .form-control": {
    backgroundColor: "#444",
    color: "#fff",
    borderColor: "#444 !important",
  },
  "& .react-tel-input .form-control+div:before": {
    color: "#fff !important",
    backgroundColor: "background.ffNavy",
  },
  "& .react-tel-input .arrow": {
    borderTop: "4px solid #fff !important",
  },
};

// was: useStyles().dividerWhite, on a <Divider>.
// `borderRadius` is written as the literal `"5px"`: `sx`'s `borderRadius`
// multiplies through `shape.borderRadius` (4 here), so the bare `5` the v4
// rule carried would emit **20px** (guide §2). `height` is pinned as a string
// for the same reason — no silent unit or scale change.
export const dividerWhiteSx: SxProps<Theme> = (theme) => ({
  backgroundColor: "white",
  borderRadius: "5px",
  height: "2px",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
});

// was: useStyles().successBox.
export const successBoxSx: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.palette.background.ffGreen,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
});

// was: useStyles().successBoxHeader.
export const successBoxHeaderSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.6rem",
  fontWeight: 600,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});
